'use client';

import {
  AIConversation,
  AIConversationContent,
  AIConversationScrollButton,
} from '@ahafood/ui/components/ai/conversation';
import {
  AIMessage,
  AIMessageAvatar,
  AIMessageContent,
} from '@ahafood/ui/components/ai/message';
import { Badge } from '@ahafood/ui/components/badge';
import { Image } from '@ahafood/ui/components/image';
import { cn } from '@ahm/common-helpers';
import { getCookie } from 'cookies-next';
import {
  type FormEventHandler,
  useEffect,
  useState,
  useRef,
  useCallback,
} from 'react';

// Import components and hooks
import {
  ChatInput,
  ChatMessage,
  ChatSuggestions,
  convertToMenuItem,
  type ItemList,
  useChatLogic,
} from './components';

import NewGreetingCard from './components/new-greeting-card';
import PersonalizeContainer from './components/personalize-container';
import {
  chatbotInteractedFn,
  useChatbotInteracted,
} from '@/api/use-chatbot-interacted';
import { useGoToOrderWebstore } from '@/api/use-webstore-order';
import { useCityRoutes } from '@/hooks/use-city-navigation';
import { useRouter } from '@/i18n/routing';
import { getAuthToken } from '@/lib/auth/get-token';
import { trackEvent } from '@/lib/event-tracking';
import { useIsMobile } from '@/lib/is-mobile';
import { useAiFoodOrderStore } from '@/store/use-ai-store';
import { useAuthStore } from '@/store/use-auth-store';

type ChatboxProps = {
  className?: string;
  threadId: string;
  enableAutoScroll?: boolean;
  useSmartScroll?: boolean;
};

export default function Chat({
  threadId,
  className,
  enableAutoScroll = false, // Disable StickToBottom auto scroll
  useSmartScroll = true, // Enable our smart scroll logic
}: ChatboxProps) {
  const token = getAuthToken();
  const router = useRouter();
  const routes = useCityRoutes();
  const { user } = useAuthStore();
  const cityId = useAiFoodOrderStore.use.city_id();
  const address = useAiFoodOrderStore.use.address();
  const [text, setText] = useState<string>('');
  const conversationRef = useRef<HTMLDivElement>(null);
  const isMobile = useIsMobile();

  // Track user interaction to prevent auto-scroll when user is actively interacting
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [lastUserInteraction, setLastUserInteraction] = useState(0);
  const [isTogglingItems, setIsTogglingItems] = useState(false);
  const userScrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Lưu trạng thái 'xem thêm' cho từng assistant message
  const [showAllItemsMap, setShowAllItemsMap] = useState<
    Record<string, boolean>
  >({});
  const goToOrderWebstore = useGoToOrderWebstore();

  // Use chat logic hook
  const { messages, sendMessage, status, setStatus, defaultSuggestions } =
    useChatLogic({
      threadId,
      user: user ?? undefined,
      cityId: cityId ?? undefined,
      address: address ?? undefined,
    });

  const { mutate: chatbotInteracted } = useChatbotInteracted({
    mutationFn: (params) =>
      chatbotInteractedFn(params, {
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      }),
  });

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const handleItemClick = async (item: ItemList) => {
    try {
      if (item.store_id) {
        chatbotInteracted({ store_id: Number(item.store_id) });
      }
      // Calculate rating (same logic as in chatItem-list)
      let rating: number | null = null;
      if (item.rating?.average && item.rating.average > 0) {
        rating = item.rating.average;
      } else if (
        item.rating?.google_average &&
        item.rating.google_average >= 4 &&
        item.rating.total === 0
      ) {
        rating = item.rating.google_average;
      }

      trackEvent('chatbot_event', {
        action_time: Math.floor(Date.now() / 1000),
        button_name: 'chatbot_item_order',
        timestamp: Date.now(),
        user_id: user?.cunstomerId,
        thread_id: threadId,

        // Store info
        store_id: item.store_id,
        rating: rating ? Number(rating.toFixed(1)) : null,
        total_sold: item.total_sold,
        distance: Number(item.distance.toFixed(1)),
        eta: item.eta,

        // Item info
        item_id: item._id,
        item_discount: item.discount_price ?? null,
        price: item.price,
        item_name: item.name,
        page_name: item.page_name,

        // Additional context
        is_solo_meal: item.extra_tags.includes('SOLO_MEAL'),
        has_discount: !!item.discount_price,
        ...((item as any).tag && { tag: (item as any).tag }),
      });
      await goToOrderWebstore(
        item.page_id.toString(),
        item.slug ?? '',
        item.nearest_branch.branch_id ?? '',
        convertToMenuItem(item)!,
        item.store_id?.toString() ?? '',
        (item as any).tag || undefined // Use the tag from item if it exists, otherwise undefined
      );
      router.push(routes.storeDetail(item.slug ?? ''));
      // Close AmoriContainer after navigation
      if (
        typeof window !== 'undefined' &&
        typeof window.setOpenChange === 'function'
      ) {
        window.setOpenChange(false);
      }
    } catch (error) {
      console.error('Failed to go to order webstore:', error);
    }
  };

  const handleSuggestionClick = (suggestion: string) => {
    // Reset user interaction state when submitting
    setIsUserScrolling(false);
    setIsTogglingItems(false);
    setLastUserInteraction(0);

    // Tracking: user bắt đầu chat với bot qua suggestion
    if (messages.filter((msg) => msg.role === 'user').length === 0) {
      trackEvent('chatbot_event', {
        action_time: Math.floor(Date.now() / 1000),
        user_id: user?.cunstomerId,
        thread_id: threadId,
        timestamp: Date.now(),
        button_name: 'chatbot_suggestion',
      });
    }
    setText(suggestion);
    setStatus('submitted');
    setTimeout(() => {
      setStatus('streaming');
    }, 200);
    void sendMessage({ text: suggestion });
    setText('');

    // Trigger scroll for messages >= 2
    if (useSmartScroll && userMessageCount >= 1) {
      setTimeout(() => {
        scrollToUserMessage(userMessageCount); // Scroll to the message that will be added
      }, 250);
    }
  };

  // Handle form submit (send message)
  const handleSubmit: FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault();
    if (!text) return;

    // Reset user interaction state when submitting
    setIsUserScrolling(false);
    setIsTogglingItems(false);
    setLastUserInteraction(0);

    if (messages.filter((msg) => msg.role === 'user').length === 0) {
      trackEvent('chatbot_event', {
        action_time: Math.floor(Date.now() / 1000),
        user_id: user?.cunstomerId,
        button_name: 'chatbot_start',
        thread_id: threadId,
        timestamp: Date.now(),
      });
    }

    setStatus('submitted');
    setTimeout(() => {
      setStatus('streaming');
    }, 200);
    void sendMessage({ text });
    setText('');

    // Trigger scroll for messages >= 2
    if (useSmartScroll && userMessageCount >= 1) {
      setTimeout(() => {
        scrollToUserMessage(userMessageCount); // Scroll to the message that will be added
      }, 250);
    }
  };

  const handleToggleShowItems = (messageId: string) => {
    // Set state to indicate we're toggling items - this completely disables auto-scroll
    setIsTogglingItems(true);

    setShowAllItemsMap((prev) => ({
      ...prev,
      [messageId]: !prev[messageId],
    }));

    // Reset the toggling state after DOM has updated
    setTimeout(() => {
      setIsTogglingItems(false);
    }, 100); // Very short timeout, just to let the DOM update finish
  };

  // Check and auto-send suggestion from AmoriBanner
  useEffect(() => {
    if (threadId) {
      const autoSuggestion = sessionStorage.getItem(
        'ahafood.chat.autoSuggestion'
      );
      if (
        autoSuggestion &&
        messages.filter((msg) => msg.role === 'user').length === 0
      ) {
        sessionStorage.removeItem('ahafood.chat.autoSuggestion');
        setTimeout(() => {
          handleSuggestionClick(autoSuggestion);
        }, 500);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  const hasUserMessages =
    messages.filter((msg) => msg.role === 'user').length > 0;
  const userMessageCount = messages.filter((msg) => msg.role === 'user').length;
  const isLoading = status === 'streaming' || status === 'submitted';

  // Custom smart scroll logic with user interaction awareness
  const scrollToUserMessage = useCallback(
    (messageIndex: number) => {
      if (!conversationRef.current || isUserScrolling || isTogglingItems)
        return;

      const conversation = conversationRef.current;
      const userMessages = conversation.querySelectorAll('[data-role="user"]');

      if (userMessages[messageIndex]) {
        const targetMessage = userMessages[messageIndex] as HTMLElement;
        targetMessage.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
          inline: 'nearest',
        });
      }
    },
    [isUserScrolling, isTogglingItems]
  );

  // Track user scroll interactions
  const handleUserScroll = useCallback(() => {
    setIsUserScrolling(true);
    setLastUserInteraction(Date.now());

    // Clear existing timeout
    if (userScrollTimeoutRef.current) {
      clearTimeout(userScrollTimeoutRef.current);
    }

    // Reset user scrolling state after 2 seconds of no interaction
    userScrollTimeoutRef.current = setTimeout(() => {
      setIsUserScrolling(false);
    }, 2000);
  }, []);

  // Setup scroll listener for user interaction detection
  useEffect(() => {
    const conversation = conversationRef.current;
    if (!conversation) return;

    conversation.addEventListener('scroll', handleUserScroll, {
      passive: true,
    });

    return () => {
      conversation.removeEventListener('scroll', handleUserScroll);
      if (userScrollTimeoutRef.current) {
        clearTimeout(userScrollTimeoutRef.current);
      }
    };
  }, [handleUserScroll]);

  // Immediate scroll when content changes (for real-time streaming)
  useEffect(() => {
    if (!useSmartScroll || !conversationRef.current || userMessageCount < 2) {
      return;
    }

    const conversation = conversationRef.current;

    // Create MutationObserver to watch for DOM changes
    const observer = new MutationObserver((mutations) => {
      // Don't auto-scroll if user is actively scrolling, recently interacted, or toggling items
      if (
        isUserScrolling ||
        isTogglingItems ||
        Date.now() - lastUserInteraction < 1000
      ) {
        return;
      }

      let shouldScroll = false;
      let isRelevantChange = false;

      mutations.forEach((mutation) => {
        // Only scroll for meaningful changes related to messages, not input changes
        if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
          // Check if added nodes are related to messages, not input field
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const element = node as Element;

              // Skip if this is within a ChatItemList (item expansion/collapse)
              if (element.closest('[data-chat-item-list="true"]')) {
                return; // Don't trigger scroll for item list changes
              }

              // Look for message-related classes or AI response indicators
              if (
                element.querySelector('[data-role]') ??
                element.classList?.contains('ai-message') ??
                element.tagName === 'SPAN' ?? // For streaming text
                element.classList?.contains('badge') // For loading indicator
              ) {
                isRelevantChange = true;
              }
            }
          });

          if (isRelevantChange) {
            shouldScroll = true;
          }
        }
        // Only track text changes in message content areas, not input
        if (
          mutation.type === 'characterData' &&
          mutation.target.parentElement
        ) {
          const parent = mutation.target.parentElement;
          // Avoid scrolling on input field changes and item list changes
          if (
            !parent.closest('input') &&
            !parent.closest('textarea') &&
            !parent.closest('[contenteditable]') &&
            !parent.closest('[data-chat-item-list="true"]') // Avoid scrolling on item list changes
          ) {
            shouldScroll = true;
          }
        }
      });

      if (shouldScroll) {
        // Small debounce to avoid too many scrolls
        clearTimeout((scrollToUserMessage as any).timeoutId);
        (scrollToUserMessage as any).timeoutId = setTimeout(() => {
          scrollToUserMessage(userMessageCount - 1);
        }, 100); // Slightly longer delay for better UX
      }
    });

    // Watch for changes in the conversation container
    observer.observe(conversation, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => {
      observer.disconnect();
      clearTimeout((scrollToUserMessage as any).timeoutId);
    };
  }, [
    useSmartScroll,
    userMessageCount,
    scrollToUserMessage,
    isUserScrolling,
    lastUserInteraction,
    isTogglingItems,
  ]);

  // Fallback scroll effect - as backup to MutationObserver
  useEffect(() => {
    // Don't auto-scroll if user is actively scrolling, recently interacted, or toggling items
    if (
      isUserScrolling ||
      isTogglingItems ||
      Date.now() - lastUserInteraction < 1000
    ) {
      return;
    }

    // Only apply as fallback when MutationObserver might miss something
    if (useSmartScroll && userMessageCount >= 2 && messages.length > 0) {
      const lastMessage = messages[messages.length - 1];

      // Scroll when user sends message (streaming starts) OR when assistant finishes
      if (
        (isLoading && lastMessage.role === 'user') || // User just sent a message and streaming started
        (!isLoading && lastMessage.role === 'assistant') // Assistant finished responding
      ) {
        // Longer delay as this is fallback, MutationObserver should handle most cases
        setTimeout(() => {
          scrollToUserMessage(userMessageCount - 1);
        }, 300);
      }
    }
  }, [
    useSmartScroll,
    userMessageCount,
    isLoading,
    messages, // Include full messages array
    scrollToUserMessage,
    isUserScrolling,
    lastUserInteraction,
    isTogglingItems,
  ]);

  return (
    <AIConversation
      className={cn(
        'flex flex-col size-full bg-white md:bg-transparent -mt-6 rounded-t-3xl z-20',
        className
      )}
      enableAutoScroll={enableAutoScroll}
    >
      <AIConversationContent
        className="flex-1 pb-12"
        enableAutoScroll={enableAutoScroll}
      >
        <div ref={conversationRef}>
          {/* Show greeting card and default suggestions only if there are no user messages */}
          {!hasUserMessages && (
            <div>
              <NewGreetingCard userName={user?.name} />
              <PersonalizeContainer
                handleItemClick={handleItemClick}
                handleSuggestionClick={handleSuggestionClick}
              />
            </div>
          )}

          {/* Messages */}
          {messages.map((msg) => {
            if (msg.role === 'user') {
              return (
                <AIMessage from="user" key={msg.id} data-role="user">
                  <AIMessageContent>
                    {(msg.parts[0] as { type: 'text'; text: string })?.text}
                  </AIMessageContent>
                  {user?.picture && (
                    <AIMessageAvatar
                      src={user?.picture}
                      name={user?.name}
                      className="bg-neutral-10 size-10"
                    />
                  )}
                </AIMessage>
              );
            }

            return (
              <ChatMessage
                key={msg.id}
                message={msg}
                showAllItemsMap={showAllItemsMap}
                onToggleShowItems={handleToggleShowItems}
                onSuggestionClick={handleSuggestionClick}
                onItemClick={handleItemClick}
                user={user ?? undefined}
                threadId={threadId}
                isLoading={isLoading}
              />
            );
          })}

          {/* Loading state */}
          {isLoading &&
            (!messages.length ||
              messages[messages.length - 1].role !== 'assistant') && (
              <Badge
                variant="secondary"
                className="py-1 flex items-center gap-1 font-bold border bg-white shadow-sm shadow-blue-40 w-fit"
              >
                <Image
                  src="/static/images/new-ai.png"
                  alt="AI Bot"
                  width={16}
                  height={16}
                  className="size-4 mr-1 object-contain"
                />
                <span className="bg-gradient-to-r from-blue-50 to-secondary-60 inline-block text-transparent bg-clip-text pr-0.5 font-bold">
                  Amori
                </span>
                <div className="flex gap-1 items-center">
                  <span className="dot-typing"></span>
                  <span className="dot-typing"></span>
                  <span className="dot-typing"></span>
                </div>
              </Badge>
            )}
        </div>
      </AIConversationContent>

      <AIConversationScrollButton
        className="bottom-32"
        enableAutoScroll={enableAutoScroll}
      />

      <div
        className={cn(
          'sticky bottom-0 bg-transparent',
          isMobile && 'bg-gradient-to-b from-white/60 via-white/90 to-white'
        )}
      >
        {!hasUserMessages && (
          <ChatSuggestions
            suggestions={defaultSuggestions}
            onSuggestionClick={handleSuggestionClick}
            isLoading={isLoading}
          />
        )}
        <ChatInput
          text={text}
          onTextChange={setText}
          onSubmit={handleSubmit}
          isLoading={isLoading}
          status={status}
        />
      </div>
    </AIConversation>
  );
}
